import { useEffect, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { Avviso } from './Pezzi.tsx'
import { Bottone, Campo, Riquadro, Scelta } from './base.tsx'
import { giorno } from './TelefoniConnessi.tsx'

/**
 * «Il calendario in Outlook e sul telefono» in «Il mio profilo» (561; docs/15-calendari-sedi.md, fase 4;
 * decisione 8 di Davide del 3/11/2026).
 *
 * Un collegamento segreto che Outlook, Google o il telefono rileggono da soli: «La mia agenda» o «Le mie
 * mediazioni», mai l'agenda di una sede. Il collegamento si vede una volta sola, appena creato (sul server ne
 * resta solo l'impronta): si copia o si apre sul telefono. L'elenco dice quando ciascuno e' stato usato
 * l'ultima volta e da quale rete, per accorgersi di un uso che non e' il proprio, e «Revoca» lo spegne.
 *
 * Il riquadro non compare a interruttore spento (`agenda_abbonamenti`) se non ci sono collegamenti, ne' a chi
 * non ha un calendario da abbonare. In palestra e impersonando si vede l'elenco, senza creare ne' revocare.
 */

type Abbonamento = {
  id: string; tipo: 'mio' | 'mediazioni'; nome: string; creato_il: string; scade_il: string
  usato_il: string | null; usi: number; ip_troncato: string | null
}
type Stato = {
  attivo: boolean; tipi: { tipo: 'mio' | 'mediazioni'; nome: string }[]; massimo: number; giorni_inutilizzo: number
  palestra: boolean; impersonando: boolean; abbonamenti: Abbonamento[]
}
type Creato = { id: string; tipo: string; nome: string; https: string; webcal: string; scade_il: string }

const NOME_TIPO: Record<Abbonamento['tipo'], string> = { mio: 'La mia agenda', mediazioni: 'Le mie mediazioni' }
const messaggio = (e: unknown) => (e instanceof ErroreApi ? e.message : 'Non è andata: riprova.')

export function CalendarioAbbonamento() {
  const [stato, setStato] = useState<Stato | null>(null)
  const [tipo, setTipo] = useState<string>('')
  const [nome, setNome] = useState('')
  const [creato, setCreato] = useState<Creato | null>(null)
  const [copiato, setCopiato] = useState<'si' | 'no' | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)

  const carica = () => api.get<Stato>('/profilo/calendari').then((s) => {
    setStato(s)
    setTipo((t) => t || s.tipi[0]?.tipo || '')
  })
  useEffect(() => { carica().catch(() => setStato(null)) }, [])

  async function crea() {
    setErrore(null); setCopiato(null); setInCorso('crea')
    try {
      setCreato(await api.post<Creato>('/profilo/calendari', { tipo, nome: nome.trim() || undefined }))
      setNome('')
      await carica()
    } catch (e) { setErrore(messaggio(e)) } finally { setInCorso(null) }
  }

  async function revoca(a: Abbonamento) {
    setErrore(null); setInCorso(a.id)
    try {
      await api.post(`/profilo/calendari/${a.id}/revoca`)
      if (creato?.id === a.id) setCreato(null)
      await carica()
    } catch (e) { setErrore(messaggio(e)) } finally { setInCorso(null) }
  }

  async function copia(testo: string) {
    try {
      await navigator.clipboard.writeText(testo)
      setCopiato('si')
    } catch {
      setCopiato('no')
    }
  }

  if (!stato || (!stato.abbonamenti.length && (!stato.attivo || !stato.tipi.length))) return null
  const puoCreare = stato.attivo && !stato.palestra && !stato.impersonando && stato.tipi.length > 0
    && stato.abbonamenti.length < stato.massimo
  return (
    <Riquadro titolo="Il calendario in Outlook e sul telefono">
      <p className="piccolo tenue">
        Un collegamento che porta i tuoi impegni nel calendario di Outlook, di Google o del telefono, e li tiene
        aggiornati da solo. Escono giorno, ora, che cosa («Turno in sede», «Mediazione CIDIS (arabo)») e la sede:
        mai i nomi delle persone, i titoli e le note. Chi ha il collegamento vede questi impegni: non condividerlo.
        Vale 12 mesi; se non lo usa nessun programma per {stato.giorni_inutilizzo} giorni si revoca da solo.
      </p>
      {!stato.attivo && (
        <Avviso>Il calendario in abbonamento è spento: i collegamenti qui sotto non mostrano niente finché non si riaccende.</Avviso>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {creato && (
        <Avviso tipo="successo">
          <p><strong>Collegamento «{creato.nome}» pronto.</strong> Lo vedi solo adesso: copialo nel programma di calendario.</p>
          <div className="riga calendario-abbonamento-collegamento">
            <input className="operatori-collegamento" readOnly value={creato.https} aria-label="Collegamento del calendario"
                   onFocus={(e) => e.currentTarget.select()} />
            <Bottone dimensione="piccolo" onClick={() => void copia(creato.https)}>
              {copiato === 'si' ? 'Copiato' : 'Copia'}
            </Bottone>
            <a className="bottone bottone-secondario bottone-piccolo" href={creato.webcal}>Apri sul telefono</a>
          </div>
          {copiato === 'no' && <p className="piccolo">Il browser non permette di copiare: seleziona il testo e copialo a mano.</p>}
          <ul className="piccolo">
            <li><strong>Outlook</strong>: Calendario → Aggiungi calendario → Da Internet (o «Sottoscrivi dal Web»), incolla il collegamento.</li>
            <li><strong>Google Calendar</strong>: Altri calendari → + → Da URL, incolla il collegamento.</li>
            <li><strong>iPhone</strong>: tocca «Apri sul telefono» e conferma «Abbonati»; oppure Impostazioni → Calendario → Account → Aggiungi account → Altro → Aggiungi calendario.</li>
            <li><strong>Android</strong>: aggiungilo da Google Calendar sul computer; il telefono lo mostra con lo stesso account.</li>
          </ul>
        </Avviso>
      )}

      {stato.abbonamenti.length > 0 && (
        <ul className="telefoni-connessi">
          {stato.abbonamenti.map((a) => (
            <li key={a.id}>
              <div>
                <strong>{a.nome}</strong>
                <span className="piccolo tenue"> · {NOME_TIPO[a.tipo]}</span>
                <div className="piccolo tenue">
                  Creato il {giorno(a.creato_il)}, {a.usato_il
                    ? `ultimo uso il ${giorno(a.usato_il)}${a.ip_troncato ? ` dalla rete ${a.ip_troncato}` : ''}`
                    : 'non ancora usato'}, vale fino al {giorno(a.scade_il)}
                </div>
              </div>
              {!stato.impersonando && (
                <Bottone variante="secondario" dimensione="piccolo" inCorso={inCorso === a.id} onClick={() => { void revoca(a) }}>
                  Revoca
                </Bottone>
              )}
            </li>
          ))}
        </ul>
      )}

      {puoCreare && (
        <div className="computer-ricordati-azione">
          <div className="griglia-modulo">
            {stato.tipi.length > 1 && (
              <Scelta etichetta="Calendario" valore={tipo} cambia={setTipo}
                      opzioni={stato.tipi.map((t) => ({ valore: t.tipo, etichetta: t.nome }))} />
            )}
            <Campo etichetta="Nome" valore={nome} cambia={setNome} maxLength={60}
                   segnaposto={tipo === 'mediazioni' ? 'Telefono' : 'Outlook dell’ufficio'}
                   aiuto="Ti serve a riconoscerlo qui, per esempio «iPhone» o «Outlook»." />
          </div>
          <Bottone variante="secondario" inCorso={inCorso === 'crea'} disabled={!tipo} onClick={() => { void crea() }}>
            Crea il collegamento{stato.tipi.length === 1 ? ` a «${stato.tipi[0].nome}»` : ''}
          </Bottone>
        </div>
      )}
      {stato.attivo && stato.abbonamenti.length >= stato.massimo && !stato.impersonando && (
        <p className="piccolo tenue">Hai {stato.massimo} collegamenti: per crearne un altro revocane uno.</p>
      )}
      {stato.palestra && <p className="piccolo tenue">In palestra non si creano collegamenti al calendario.</p>}
    </Riquadro>
  )
}
